import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  TrendingUp, RefreshCw, Plus, ExternalLink, Eye,
  Video, Clock, Loader2, AlertCircle, CheckCircle, Music, Tag,
  Heart, User, Play
} from 'lucide-react';
import toast from 'react-hot-toast';

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtViews(n) {
  if (!n) return '—';
  n = Number(n);
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
  if (n >= 1_000) return (n / 1_000).toFixed(1).replace(/\.0$/, '') + 'K';
  return n.toLocaleString();
}

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric'
  });
}

// Source label → display text & colour
const SOURCE_META = {
  csi:            { label: 'CSI',            bg: 'bg-blue-100 text-blue-800' },
  keyword_search: { label: 'Keyword Search', bg: 'bg-purple-100 text-purple-800' },
  seed_account:   { label: 'Seed Account',   bg: 'bg-orange-100 text-orange-800' },
  showstop_seed:  { label: 'ShowStop Seed',  bg: 'bg-pink-100 text-pink-800' },
  audio_search:   { label: 'Audio Search',   bg: 'bg-teal-100 text-teal-800' },
};

// Tier badge colours
const TIER_META = {
  A:      { label: 'Tier A', bg: 'bg-green-100 text-green-800 border-green-200' },
  B:      { label: 'Tier B', bg: 'bg-blue-100 text-blue-800 border-blue-200' },
  C:      { label: 'Tier C', bg: 'bg-yellow-100 text-yellow-800 border-yellow-200' },
  search: { label: 'Search', bg: 'bg-purple-100 text-purple-800 border-purple-200' },
};

// LLM category colours
const CATEGORY_META = {
  good:          { label: 'good',          bg: 'bg-green-50 text-green-700' },
  single_event:  { label: 'single event',  bg: 'bg-teal-50 text-teal-700' },
  niche_topic:   { label: 'niche topic',   bg: 'bg-gray-100 text-gray-600' },
  commentary:    { label: 'commentary',    bg: 'bg-red-50 text-red-700' },
  show_clip:     { label: 'show clip',     bg: 'bg-red-50 text-red-700' },
  too_generic:   { label: 'too generic',   bg: 'bg-red-50 text-red-700' },
  meta_trend:    { label: 'meta trend',    bg: 'bg-red-50 text-red-700' },
  ai_animation:  { label: 'AI animation',  bg: 'bg-red-50 text-red-700' },
};

// Tab definitions (CSI hidden — low signal-to-noise)
const TABS = [
  { key: 'all',            label: 'All' },
  { key: 'keyword_search', label: 'Keyword Search' },
  { key: 'seed_account',   label: 'Seed Accounts' },
  { key: 'showstop_seed',  label: 'ShowStop Seeds' },
  { key: 'audio_search',   label: 'Audio Search' },
];

// ── Card component ────────────────────────────────────────────────────────────

function CandidateCard({ candidate, onAdd }) {
  const [adding, setAdding] = useState(false);  // true while submit + polling
  const [added, setAdded] = useState(false);

  const tier = candidate.tier || 'C';
  const tierMeta = TIER_META[tier] || TIER_META.C;
  const srcMeta = SOURCE_META[candidate.source] || { label: candidate.source, bg: 'bg-gray-100 text-gray-700' };
  const catMeta = candidate.llm_category ? (CATEGORY_META[candidate.llm_category] || null) : null;

  async function handleAdd() {
    setAdding(true);
    try {
      await onAdd(candidate);  // handles submit + polling + toast internally
      setAdded(true);
    } catch (err) {
      toast.error(err.message || 'Failed to add trend');
    } finally {
      setAdding(false);
    }
  }

  const videos = candidate.videos || [];
  const firstUrl = videos.length > 0 ? videos[0].url : null;

  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4 flex flex-col gap-3 shadow-sm hover:shadow-md transition-shadow">
      {/* Top row: tier badge + source chip */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${tierMeta.bg}`}>
          {tierMeta.label}
        </span>
        <span className={`text-xs px-2 py-0.5 rounded-full ${srcMeta.bg}`}>
          {srcMeta.label}
        </span>
        {catMeta && (
          <span className={`text-xs px-2 py-0.5 rounded-full ${catMeta.bg}`}>
            {catMeta.label}
          </span>
        )}
        <span className="ml-auto text-xs text-gray-400">#{candidate.rank}</span>
      </div>

      {/* Trend name */}
      <h3 className="font-semibold text-gray-900 dark:text-white leading-snug line-clamp-2">
        {candidate.trend_name}
      </h3>

      {/* Sound title */}
      {candidate.sound_title && (
        <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
          <Music className="w-3 h-3 flex-shrink-0" />
          <span className="truncate">{candidate.sound_title}</span>
        </div>
      )}

      {/* Stats row */}
      <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400 flex-wrap">
        <span className="flex items-center gap-1">
          <Eye className="w-3 h-3" />
          {fmtViews(candidate.total_views)}
        </span>
        <span className="flex items-center gap-1">
          <Video className="w-3 h-3" />
          {candidate.video_count || 0} videos
        </span>
        {candidate.newest_days_old != null && (
          <span className="flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {candidate.newest_days_old}d old
          </span>
        )}
        {candidate.rank_score != null && (
          <span className="flex items-center gap-1 ml-auto">
            <Tag className="w-3 h-3" />
            {(candidate.rank_score * 100).toFixed(0)}%
          </span>
        )}
      </div>

      {/* Video URLs (up to 3 shown as links) */}
      {videos.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {videos.slice(0, 3).map((v, i) => (
            <a
              key={i}
              href={v.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-blue-500 hover:text-blue-700 underline flex items-center gap-0.5"
            >
              <ExternalLink className="w-3 h-3" />
              Video {i + 1}
            </a>
          ))}
          {videos.length > 3 && (
            <span className="text-xs text-gray-400">+{videos.length - 3} more</span>
          )}
        </div>
      )}

      {/* Add to ShowStop button — only for sources with real video URLs */}
      {candidate.source !== 'keyword_search' && (
        <button
          onClick={handleAdd}
          disabled={adding || added || videos.length < 3}
          title={videos.length < 3 ? 'Need ≥3 video URLs to submit' : 'Add to ShowStop Trend Spotter'}
          className={`mt-auto flex items-center justify-center gap-2 w-full py-2 px-3 rounded-lg text-sm font-medium transition-colors
            ${added
              ? 'bg-green-50 text-green-700 border border-green-200 cursor-default'
              : adding
                ? 'bg-yellow-50 text-yellow-700 border border-yellow-200 cursor-wait'
                : videos.length < 3
                  ? 'bg-gray-50 text-gray-400 border border-gray-200 cursor-not-allowed'
                  : 'bg-indigo-50 hover:bg-indigo-100 text-indigo-700 border border-indigo-200 cursor-pointer'
            }`}
        >
          {added ? (
            <><CheckCircle className="w-4 h-4" /> Added to ShowStop</>
          ) : adding ? (
            <><Loader2 className="w-4 h-4 animate-spin" /> Processing (~5 min)…</>
          ) : (
            <><Plus className="w-4 h-4" /> Add to ShowStop</>
          )}
        </button>
      )}
    </div>
  );
}

// ── Video sources (show VideoCard, not CandidateCard) ─────────────────────────
const VIDEO_SOURCES = new Set(['keyword_search', 'seed_account', 'showstop_seed']);

// ── VideoCard — one card per individual video (for keyword/seed/showstop tabs) ─

function VideoCard({ candidate }) {
  const video    = (candidate.videos || [])[0] || {};
  const videoUrl = video.url || '';
  const platform = video.platform || (videoUrl.includes('instagram.com') ? 'instagram' : 'tiktok');
  const srcMeta  = SOURCE_META[candidate.source] || { label: candidate.source, bg: 'bg-gray-100 text-gray-700' };
  const keyword  = candidate.keyword || '';

  // Use stored thumbnail_url first; fall back to TikTok oEmbed if missing (skip for Instagram)
  const storedThumb = video.thumbnail_url || '';
  const [oembedThumb, setOembedThumb] = useState(null);
  useEffect(() => {
    if (storedThumb || !videoUrl || platform === 'instagram') return;  // skip oEmbed for Instagram
    let cancelled = false;
    fetch(`/api/trend-scan/oembed?url=${encodeURIComponent(videoUrl)}`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (!cancelled && d?.thumbnail_url) setOembedThumb(d.thumbnail_url); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [videoUrl, storedThumb]);

  const thumb = storedThumb || oembedThumb;

  // Prefer video-level views; fall back to candidate total_views
  const views    = video.views || candidate.total_views || 0;
  const likes    = video.likes || 0;
  const daysOld  = video.days_old ?? candidate.newest_days_old;
  const creator  = video.creator || '';
  const caption  = video.caption || candidate.trend_name || '';

  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden flex flex-col shadow-sm hover:shadow-md transition-shadow">
      {/* Thumbnail */}
      <a href={videoUrl || '#'} target="_blank" rel="noopener noreferrer"
         className="block relative bg-gray-100 dark:bg-gray-700 flex-shrink-0"
         style={{aspectRatio:'9/16', maxHeight:'220px', overflow:'hidden'}}>
        {thumb
          ? <img src={thumb} alt="thumbnail" className="w-full h-full object-cover" loading="lazy"
                 onError={e => { e.target.style.display='none'; }} />
          : <div className="w-full h-full flex flex-col items-center justify-center gap-2">
              {platform === 'instagram'
                ? <svg viewBox="0 0 24 24" className="w-10 h-10" fill="none" stroke="url(#ig)" strokeWidth="1.5">
                    <defs><linearGradient id="ig" x1="0%" y1="100%" x2="100%" y2="0%"><stop offset="0%" stopColor="#f09433"/><stop offset="25%" stopColor="#e6683c"/><stop offset="50%" stopColor="#dc2743"/><stop offset="75%" stopColor="#cc2366"/><stop offset="100%" stopColor="#bc1888"/></linearGradient></defs>
                    <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/><circle cx="12" cy="12" r="4"/><circle cx="17.5" cy="6.5" r="1" fill="#cc2366" stroke="none"/>
                  </svg>
                : <svg viewBox="0 0 48 48" className="w-10 h-10 text-gray-300 dark:text-gray-600" fill="currentColor">
                    <path d="M41 9.8C38.5 9.3 36.3 7.9 34.6 5.9c-.4-.5-1-.8-1.6-.8h-6c-.8 0-1.5.7-1.5 1.5V32c0 2.8-2.2 5-5 5s-5-2.2-5-5 2.2-5 5-5c.5 0 1 .1 1.5.2V20c-.5-.1-1-.1-1.5-.1-7.2 0-13 5.8-13 13s5.8 13 13 13 13-5.8 13-13V18.3c2.3 1.3 4.9 2 7.5 2 .8 0 1.5-.7 1.5-1.5v-7.5c0-.8-.6-1.4-1.5-1.5z"/>
                  </svg>
              }
              {creator && <span className="text-xs font-medium text-gray-400">@{creator}</span>}
            </div>
        }
        {/* Play overlay on hover */}
        <div className="absolute inset-0 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity bg-black/20">
          <div className="bg-white/90 rounded-full p-3">
            <svg viewBox="0 0 24 24" className="w-6 h-6 text-gray-900" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
          </div>
        </div>
      </a>

      {/* Info */}
      <div className="p-3 flex flex-col gap-1.5 flex-1">
        {/* Source + keyword badge */}
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className={`text-xs px-2 py-0.5 rounded-full ${srcMeta.bg}`}>{srcMeta.label}</span>
          {keyword && <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 truncate max-w-[130px]" title={keyword}>{keyword}</span>}
        </div>

        {/* Creator */}
        {creator && (
          <p className="text-sm font-semibold text-gray-800 dark:text-gray-200 truncate">@{creator}</p>
        )}

        {/* Caption */}
        {caption && (
          <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2">{caption}</p>
        )}

        {/* Stats row */}
        <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400 mt-auto pt-1 flex-wrap">
          <span className="flex items-center gap-0.5"><Eye className="w-3 h-3"/>{fmtViews(views)}</span>
          {likes > 0 && <span className="flex items-center gap-0.5"><Heart className="w-3 h-3"/>{fmtViews(likes)}</span>}
          {daysOld != null && <span className="flex items-center gap-0.5"><Clock className="w-3 h-3"/>{daysOld}d</span>}
        </div>

        {/* Watch link */}
        {videoUrl && (
          <a href={videoUrl} target="_blank" rel="noopener noreferrer"
             className="mt-1 flex items-center justify-center gap-1.5 w-full py-1.5 px-3 rounded-lg text-xs font-medium bg-gray-50 hover:bg-gray-100 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-600 transition-colors">
            <ExternalLink className="w-3 h-3" /> {platform === 'instagram' ? 'Watch on Instagram' : 'Watch on TikTok'}
          </a>
        )}
      </div>
    </div>
  );
}

// ── ShowStop API constants ─────────────────────────────────────────────────────
const SS_BASE    = 'https://app.showstop.ai';
const SS_API_KEY = '9a2b9c4da3b386fc4e6b5c24cd2ba26f02398a00597b4e5c245db6819307c4dc';
const SS_USER_ID = 'info@showstop.ai';

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function TrendScanPage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [scanMeta, setScanMeta] = useState({ scan_id: null, run_date: null, sources: [] });
  const [candidates, setCandidates] = useState([]);
  const [activeTab, setActiveTab] = useState('all');

  // ── Fetch latest scan ──────────────────────────────────────────────────────
  const fetchLatest = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/trend-scan/latest', {
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setScanMeta({ scan_id: data.scan_id, run_date: data.run_date, sources: data.sources || [] });
      setCandidates(data.candidates || []);
    } catch (err) {
      console.error('Error fetching trend scan:', err);
      setError('Failed to load trend scan results.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchLatest(); }, [fetchLatest]);

  // ── Add candidate to ShowStop (submit + poll job) ─────────────────────────
  async function handleAddToShowStop(candidate) {
    const videos = candidate.videos || [];
    const urls = videos.map(v => v.url).filter(Boolean).slice(0, 5);
    if (urls.length < 3) throw new Error('Need at least 3 video URLs to submit');

    // 1. Submit
    const res = await fetch(`${SS_BASE}/api/trend-spotter/external/videos-and-trend`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'x-api-key': SS_API_KEY },
      body: JSON.stringify({ urls, trendName: candidate.trend_name, userId: SS_USER_ID }),
    });
    const data = await res.json();
    if (!data.success) throw new Error(data.error || data.message || 'Submission failed');

    const jobId = data.jobId;
    if (!jobId) return data; // no job to poll — treat as done

    // 2. Poll until complete (max 10 min, 8 s interval)
    const toastId = toast.loading(`Submitting "${candidate.trend_name}" to ShowStop…`);
    const deadline = Date.now() + 10 * 60 * 1000;
    while (Date.now() < deadline) {
      await new Promise(r => setTimeout(r, 8000));
      try {
        const jr = await fetch(`${SS_BASE}/api/trend-spotter/jobs/${jobId}`, {
          headers: { 'x-api-key': SS_API_KEY },
        });
        const jd = await jr.json();
        const status = (jd.status || jd.job?.status || '').toLowerCase();
        if (status === 'completed' || status === 'done' || jd.templateId || jd.job?.templateId) {
          toast.success(`"${candidate.trend_name}" added to ShowStop ✓`, { id: toastId });
          return jd;
        }
        if (status === 'failed' || status === 'error') {
          toast.dismiss(toastId);
          throw new Error(jd.error || jd.message || 'ShowStop job failed');
        }
      } catch (pollErr) {
        if (pollErr.message.includes('job failed')) { toast.dismiss(toastId); throw pollErr; }
        // else ignore transient network errors
      }
    }
    toast.dismiss(toastId);
    throw new Error('Timed out waiting for ShowStop to process the trend');
  }

  // ── Filter by tab ──────────────────────────────────────────────────────────
  const filtered = activeTab === 'all'
    ? candidates
    : candidates.filter(c => c.source === activeTab);

  // ── Tab counts ─────────────────────────────────────────────────────────────
  function tabCount(key) {
    if (key === 'all') return candidates.length;
    return candidates.filter(c => c.source === key).length;
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
      {/* Header */}
      <div className="max-w-7xl mx-auto mb-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <TrendingUp className="w-7 h-7 text-indigo-600" />
              Daily Trend Scan
            </h1>
            {scanMeta.run_date && (
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Last scan: {fmtDate(scanMeta.run_date)}
                {scanMeta.sources && scanMeta.sources.length > 0 && (
                  <span className="ml-2">
                    {scanMeta.sources.map(s => (
                      <span key={s} className={`inline-block text-xs px-2 py-0.5 rounded-full mr-1 ${(SOURCE_META[s] || {}).bg || 'bg-gray-100 text-gray-700'}`}>
                        {(SOURCE_META[s] || {}).label || s}
                      </span>
                    ))}
                  </span>
                )}
              </p>
            )}
          </div>
          <button
            onClick={fetchLatest}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="max-w-7xl mx-auto mb-4">
        <div className="flex gap-1 bg-white dark:bg-gray-800 rounded-xl p-1 border border-gray-200 dark:border-gray-700 w-fit flex-wrap">
          {TABS.map(tab => {
            const count = tabCount(tab.key);
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors whitespace-nowrap
                  ${activeTab === tab.key
                    ? 'bg-indigo-600 text-white shadow-sm'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
              >
                {tab.label}
                {count > 0 && (
                  <span className={`ml-1.5 text-xs px-1.5 py-0.5 rounded-full ${activeTab === tab.key ? 'bg-indigo-500 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'}`}>
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto">
        {loading && (
          <div className="flex flex-col items-center justify-center py-24 text-gray-500">
            <Loader2 className="w-10 h-10 animate-spin mb-3 text-indigo-500" />
            <p className="text-sm">Loading trend scan…</p>
          </div>
        )}

        {!loading && error && (
          <div className="flex flex-col items-center justify-center py-24 text-gray-500">
            <AlertCircle className="w-10 h-10 mb-3 text-red-400" />
            <p className="text-sm text-red-600">{error}</p>
            <button onClick={fetchLatest} className="mt-4 text-sm text-indigo-600 hover:underline">
              Try again
            </button>
          </div>
        )}

        {!loading && !error && candidates.length === 0 && (
          <div className="flex flex-col items-center justify-center py-24 text-gray-400">
            <TrendingUp className="w-12 h-12 mb-4 opacity-30" />
            <p className="text-base font-medium text-gray-600 dark:text-gray-400">No scan results yet</p>
            <p className="text-sm mt-1 text-gray-500">
              Run <code className="bg-gray-100 px-1 rounded">main.py</code> on your Mac to start the daily scan.
            </p>
          </div>
        )}

        {!loading && !error && filtered.length === 0 && candidates.length > 0 && (
          <div className="flex flex-col items-center justify-center py-24 text-gray-400">
            <p className="text-sm text-gray-500">No {activeTab} candidates in this scan.</p>
          </div>
        )}

        {!loading && !error && filtered.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filtered.map((candidate, i) => (
              VIDEO_SOURCES.has(candidate.source)
                ? <VideoCard key={`${candidate.source}-${candidate.rank || i}`} candidate={candidate} />
                : <CandidateCard key={`${candidate.source}-${candidate.rank || i}`} candidate={candidate} onAdd={handleAddToShowStop} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
